import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Send, X } from "lucide-react";
import { ChasedPill, DpdPill, LegalPill, Pill, RestructuredPill, gbp } from "./pills";

export const baseSf = (sf) => { const m = (sf || "").toUpperCase().match(/SF[\s\-/_.]*(\d{3,6})/); return m ? `SF-${m[1]}` : sf; };

export const QuietTag = ({ children, tone = "slate", testId }) => (
  <span data-testid={testId} className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-tight ${
    tone === "amber" ? "border-amber-200/70 bg-amber-50 text-amber-700" : tone === "emerald" ? "border-emerald-200/70 bg-emerald-50 text-emerald-700" : "border-slate-200/60 bg-slate-100/80 text-slate-500"}`}>
    {children}
  </span>
);

export function SearchBox({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-8 pl-8 text-xs" data-testid={testId} />
      {value && (
        <button type="button" onClick={() => onChange("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Clear">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// Dean, 9 Oct 2026: one lump of 90 loans told the team nothing — split by what the row really is.
export const SECTIONS = [
  { key: "expected", label: "Expected today", hint: "Up to date — money normally lands later in the day" },
  { key: "arrears", label: "In arrears", hint: "Also behind on earlier instalments" },
  { key: "legal", label: "Never paid / with Legal", hint: "No repayment ever posted, or the file sits with Legal" },
];

export const sectionOf = (l, f) => {
  const behind = (l.past_due || 0) > 0 || (l.days_past_due || 0) > 0 || (!!f?.on_arrears_list && (f?.arrears_amount || 0) > 0);
  if (f?.legal || (behind && !(l.paid_amount > 0))) return "legal";
  return behind ? "arrears" : "expected";
};

const arrearsOf = (l, f) => (f?.in_arrears ? f.arrears_amount : l.past_due) || 0;

function LoanRow({ l, f, ch, onFindMatch }) {
  const partPaid = (l.already_covered || 0) > 0;
  return (
    <tr className="group border-b hover:bg-slate-50" data-testid={`row-awaiting-${l.sf_number}`}>
      <td className="px-3 py-2 align-top">
        <div className="font-medium leading-tight text-slate-900">
          {f?.case_id ? <Link to={`/collections/ageing/${f.case_id}`} className="hover:underline hover:text-emerald-700" data-testid={`btn-open-file-${l.sf_number}`}>{l.borrower_name}</Link> : l.borrower_name}
        </div>
        {(f?.legal || ch || partPaid) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {f?.legal && <LegalPill handler={f.handler} testId={`loan-legal-pill-${l.sf_number}`} />}
            {partPaid && <Pill tone="sky" testId={`loan-part-paid-${l.sf_number}`} title={`LoanDisk shows ${gbp(l.already_covered)} of today's ${gbp(l.scheduled_amount)} already received`}>Part-paid · {gbp(l.already_covered)} received</Pill>}
            {ch && <ChasedPill channel={ch.channel} testId={`loan-chased-badge-${l.sf_number}`} />}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top font-mono text-xs text-muted-foreground">
        <div className="flex flex-col items-start gap-1">
          <span>{l.sf_number}</span>
          {f?.restructured && <RestructuredPill iteration={f.iteration} testId={`loan-restructured-pill-${l.sf_number}`} />}
        </div>
      </td>
      <td className="px-3 py-2 align-top text-center"><DpdPill dpd={l.days_past_due} /></td>
      <td className="px-3 py-2 align-top text-right tabular-nums text-rose-700 font-medium">{arrearsOf(l, f) > 0 ? gbp(arrearsOf(l, f)) : <span className="text-slate-300">—</span>}</td>
      <td className="px-3 py-2 align-top text-right tabular-nums font-semibold text-slate-900">
        {gbp(l.due_amount)}
        {partPaid && <div className="text-[10px] font-normal text-slate-400">of {gbp(l.scheduled_amount)}</div>}
      </td>
      <td className="px-2 py-1.5 align-top text-right">
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-slate-500 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-emerald-700" onClick={() => onFindMatch(l)} data-testid={`btn-find-match-${l.sf_number}`}>
          Find match
        </Button>
      </td>
    </tr>
  );
}

export function AwaitingPaymentTable({ loans, chased, facts, onFindMatch, onPrechase, total, partPaidCount }) {
  const [q, setQ] = useState("");
  const rows = loans
    .filter((l) => !q || `${l.borrower_name} ${l.sf_number} ${l.due_amount}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.due_amount || 0) - (a.due_amount || 0));
  const groups = SECTIONS.map((s) => ({ ...s, rows: rows.filter((l) => sectionOf(l, facts?.[l.sf_number]) === s.key) })).filter((g) => g.rows.length);
  const chasedCount = Object.keys(chased || {}).length;
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="awaiting-payment-panel">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
        <div className="mr-auto">
          <h2 className="text-base font-semibold text-slate-900">Awaiting payment</h2>
          <p className="text-xs text-slate-500" data-testid="awaiting-summary">
            {loans.length} loans · <span className="font-mono tabular-nums text-amber-700">{gbp(total)}</span> still due
            {partPaidCount ? ` · ${partPaidCount} part-paid` : ""}{chasedCount ? ` · ${chasedCount} chased today` : ""}
          </p>
        </div>
        <div className="w-56"><SearchBox value={q} onChange={setQ} placeholder="Name, SF or amount" testId="awaiting-search" /></div>
        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={onPrechase} disabled={!loans.length} data-testid="prechase-open-btn">
          <Send className="w-3.5 h-3.5 mr-1.5" />Pre-chase
        </Button>
      </header>
      <div className="max-h-[620px] overflow-auto">
        <table className="w-full text-sm" data-testid="table-awaiting-payment">
          <thead className="sticky top-0 z-10 border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Customer</th>
              <th className="px-3 py-2 text-left font-semibold">SF#</th>
              <th className="px-3 py-2 text-center font-semibold">DPD</th>
              <th className="px-3 py-2 text-right font-semibold">Arrears £</th>
              <th className="px-3 py-2 text-right font-semibold">Due today £</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <SectionRows key={g.key} group={g} chased={chased} facts={facts} onFindMatch={onFindMatch} />
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="awaiting-empty">{q ? "No loans match the search" : "Every loan due today has been paid"}</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 border-t-2 border-slate-300 bg-slate-50">
              <tr className="text-sm font-semibold" data-testid="awaiting-totals-row">
                <td className="px-3 py-2.5" colSpan={3}><span className="text-[10px] uppercase tracking-wider text-slate-500">Totals · </span><span className="text-slate-700">{rows.length} {rows.length === 1 ? "loan" : "loans"}</span></td>
                <td className="px-3 py-2.5 text-right tabular-nums text-rose-700">{gbp(rows.reduce((s, l) => s + arrearsOf(l, facts?.[l.sf_number]), 0))}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-900">{gbp(rows.reduce((s, l) => s + (l.due_amount || 0), 0))}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

function SectionRows({ group, chased, facts, onFindMatch }) {
  const due = group.rows.reduce((s, l) => s + (l.due_amount || 0), 0);
  return (
    <>
      <tr className="border-b bg-slate-100/70" data-testid={`awaiting-section-${group.key}`}>
        <td colSpan={6} className="px-3 py-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">{group.label}</span>
          <span className="ml-2 font-mono text-[11px] tabular-nums text-slate-500" data-testid={`awaiting-section-count-${group.key}`}>{group.rows.length} · {gbp(due)}</span>
          <span className="ml-2 text-[11px] text-slate-400">{group.hint}</span>
        </td>
      </tr>
      {group.rows.map((l) => (
        <LoanRow key={l.sf_number} l={l} f={facts?.[l.sf_number]} ch={chased?.[baseSf(l.sf_number)]?.[0]} onFindMatch={onFindMatch} />
      ))}
    </>
  );
}
